import { Helmet } from 'react-helmet-async'
import { FadeIn } from '@components/animations/FadeIn/FadeIn'
import { CheckCircle, Award, Clock, Shield, Users, Lightbulb } from 'lucide-react'

const advantages = [
  {
    icon: Award,
    title: 'End-to-End Capabilities',
    description: 'Unlike traditional vendors, we handle everything - Design, Development, Deployment, Integration, Maintenance, and Transformation. Single point of accountability.',
  },
  {
    icon: CheckCircle,
    title: 'ISO Certified Excellence',
    description: '5 ISO certifications (9001, 14001, 27001, 45001, 20000) ensuring quality, security, environmental responsibility, and service excellence.',
  },
  {
    icon: Clock,
    title: '15+ Years Track Record',
    description: 'Proven expertise with 50+ successful projects across Government, Education, Healthcare, and Infrastructure sectors.',
  },
  {
    icon: Shield,
    title: 'Government Domain Expertise',
    description: 'Deep understanding of government processes, compliance requirements, and public sector dynamics.',
  },
  {
    icon: Users,
    title: 'Strong Partner Network',
    description: 'Strategic partnerships with leading OEMs, technology providers, and implementation agencies.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation-First Approach',
    description: 'Continuous R&D investment in AI/ML, IoT, and emerging technologies to stay ahead of the curve.',
  },
]

const Advantage = () => {
  return (
    <>
      <Helmet>
        <title>Why Choose Us | Eonixa Dynamics</title>
        <meta name="description" content="Our competitive advantage - End-to-end capabilities, ISO certifications, 15+ years experience, and government expertise" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-32 pb-20">
        <div className="section-container">
          <FadeIn>
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold text-gray-900">Competitive Advantage</h1>
              <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                What makes Eonixa Dynamics the preferred choice for Government Technology
                and Infrastructure projects.
              </p>
            </div>
          </FadeIn>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {advantages.map((advantage, index) => {
              const Icon = advantage.icon
              return (
                <FadeIn key={advantage.title} delay={index * 0.1}>
                  <div className="group h-full rounded-2xl bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-card">
                    <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-primary-50 text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mb-3 text-lg font-bold text-gray-900">
                      {advantage.title}
                    </h3>
                    <p className="text-gray-600">{advantage.description}</p>
                  </div>
                </FadeIn>
              )
            })}
          </div>

          <FadeIn delay={0.6}>
            <div className="mt-16 rounded-2xl bg-white p-8 shadow-soft">
              <h2 className="mb-8 text-center text-2xl font-bold text-gray-900">
                Our Approach vs Traditional Vendors
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="py-4 text-left text-gray-900 font-semibold">Parameter</th>
                      <th className="py-4 text-center text-primary-600 font-semibold bg-primary-50 rounded-t-lg">Eonixa Dynamics</th>
                      <th className="py-4 text-center text-gray-600 font-semibold">Traditional Vendors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Scope', 'End-to-End Solution', 'Product/Service Only'],
                      ['Accountability', 'Single Point', 'Multiple Vendors'],
                      ['Certifications', '5 ISO Certified', 'Limited/Legacy'],
                      ['Domain Expertise', '15+ Years Govt. Projects', 'Limited Exposure'],
                      ['Support', '24/7 Managed Services', 'Business Hours Only'],
                      ['Innovation', 'Continuous R&D', 'Static Offerings'],
                    ].map((row, index) => (
                      <tr key={index} className="border-b last:border-0">
                        <td className="py-4 font-medium text-gray-900">{row[0]}</td>
                        <td className="py-4 text-center text-green-600 font-medium bg-primary-50/50">
                          {row[1]}
                        </td>
                        <td className="py-4 text-center text-gray-600">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </>
  )
}

export default Advantage
